import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Route protection component that only allows Administrator users to access protected pages
 * Shows access denied message for non-admin users
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { userRole, userPrincipalName, isCheckingAuthorization } = useAuth();

  // Show loading while checking authorization
  if (isCheckingAuthorization) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-300 mt-4">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Show access denied for non-administrators
  if (userRole !== 'Administrator') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
            <div className="flex justify-center mb-6">
              <Shield className="w-16 h-16 text-red-500" />
            </div>
            
            <h1 className="text-xl font-semibold text-white mb-4">
              Administrator Access Required
            </h1>
            
            <p className="text-gray-300 mb-6">
              This page is restricted to users with Administrator privileges. 
              Your current role is: <span className="font-medium text-blue-400">{userRole || 'User'}</span>
            </p>
            
            {userPrincipalName && (
              <p className="text-sm text-gray-500 mb-6">
                Signed in as: {userPrincipalName}
              </p>
            )}
            
            <div className="space-y-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Dashboard
              </Link>
              
              <p className="text-xs text-gray-500">
                Contact your system administrator if you believe you should have access to this page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User is administrator, render the protected content
  return <>{children}</>;
};